import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TooltipModule } from 'primeng/tooltip';
import { FaInviteStateService } from '../fa-invite-state.service';
import {
  FaAvailability,
  FaProfile,
  FaSpecialty,
  filterFaProfiles,
  formatTenure,
} from '../fa-invite.model';

export interface InviteSentEvent {
  faId: string;
  reason: string | undefined;
}

const REASON_MAX_LENGTH = 240;
const SEARCH_DEBOUNCE_MS = 250;

@Component({
  selector: 'bai-fa-invite-modal',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TooltipModule],
  templateUrl: './fa-invite-modal.component.html',
  styleUrl: './fa-invite-modal.component.scss',
})
export class FaInviteModalComponent implements OnDestroy {
  private readonly _inviteState = inject(FaInviteStateService);

  readonly sessionId = input.required<string>();
  readonly inviterName = input<string>('Financial Assistant');

  readonly close = output<void>();
  readonly inviteSent = output<InviteSentEvent>();

  readonly reasonMaxLength = REASON_MAX_LENGTH;

  readonly searchQuery = signal('');
  readonly selectedFaId = signal<string | null>(null);
  readonly reason = signal('');
  readonly filterAvailableOnly = signal(false);

  constructor() {
    // Ensure session state exists before any computed reads it.
    effect(() => {
      const id = this.sessionId();
      if (id) this._inviteState.getSessionState(id);
    });
  }

  private _searchTimer: ReturnType<typeof setTimeout> | null = null;
  /** Debounced version of searchQuery used for actual filtering. */
  readonly debouncedQuery = signal('');

  readonly profiles = computed(() => {
    let list = this._inviteState.allProfiles;
    if (this.filterAvailableOnly()) {
      list = list.filter(p => p.availability === 'available');
    }
    return filterFaProfiles(list, this.debouncedQuery());
  });

  readonly selectedFa = computed<FaProfile | null>(() => {
    const id = this.selectedFaId();
    if (!id) return null;
    return this._inviteState.allProfiles.find(p => p.id === id) ?? null;
  });

  readonly canSend = computed(() => {
    const id = this.selectedFaId();
    if (!id) return false;
    const status = this._inviteState.getInviteStatus(this.sessionId(), id);
    return status === 'none';
  });

  readonly reasonTooLong = computed(() => this.reason().length > REASON_MAX_LENGTH);

  onSearchInput(value: string): void {
    this.searchQuery.set(value);
    if (this._searchTimer) clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(() => {
      this.debouncedQuery.set(value);
    }, SEARCH_DEBOUNCE_MS);
  }

  selectFa(faId: string): void {
    this.selectedFaId.set(faId);
  }

  send(): void {
    const id = this.selectedFaId();
    if (!id || !this.canSend()) return;
    const reason = this.reason().trim() || undefined;
    this.inviteSent.emit({ faId: id, reason });
  }

  toggleAvailableFilter(): void {
    this.filterAvailableOnly.update(v => !v);
  }

  cancel(): void {
    this.close.emit();
  }

  getInviteStatus(faId: string): ReturnType<FaInviteStateService['getInviteStatus']> {
    return this._inviteState.getInviteStatus(this.sessionId(), faId);
  }

  availabilityLabel(avail: FaAvailability): string {
    switch (avail) {
      case 'available': return 'Available';
      case 'busy':      return 'Busy';
      case 'offline':   return 'Offline';
    }
  }

  availabilityClass(avail: FaAvailability): string {
    switch (avail) {
      case 'available': return 'bai-invite__avail--available';
      case 'busy':      return 'bai-invite__avail--busy';
      case 'offline':   return 'bai-invite__avail--offline';
    }
  }

  formatTenure(months: number): string {
    return formatTenure(months);
  }

  specialtyLabel(s: FaSpecialty): string { return s; }

  ngOnDestroy(): void {
    if (this._searchTimer) clearTimeout(this._searchTimer);
  }
}
